// src/pages/profile-settings/doctor/PracticeTeamInviteLanding.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import supabase from "@/services/supabaseClient";
import { callFunctionRaw } from "@/lib/callFunction.js";
import Button from "@/components/ui/Button.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

function readInviteParams(search = "") {
  try {
    const params = new URLSearchParams(search || "");
    return {
      email: String(params.get("email") || "").trim().toLowerCase(),
      proof: String(params.get("proof") || "") === "1",
      token: String(params.get("token") || "").trim(),
    };
  } catch {
    return { email: "", proof: false, token: "" };
  }
}

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

export default function PracticeTeamInviteLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const invite = React.useMemo(() => readInviteParams(location.search), [location.search]);
  const returnPath = "/settings/doctor/preferences/practice-team";
  const backPath = invite.proof ? returnPath : "/login";
  const [inviteDetails, setInviteDetails] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function openInvite() {
      setLoading(true);
      setError("");
      try {
        const result = await callFunctionRaw(
          "practice-team-invite-lifecycle",
          {
            action: "open",
            token: invite.token,
            email: invite.email,
            proof: invite.proof,
          },
          { timeoutMs: 10000 },
        );
        if (!alive) return;
        setInviteDetails(result?.invite || null);
      } catch {
        if (!alive) return;
        setError(
          t(
            "settings.practiceTeam.invite.errors.lookup",
            isFrench
              ? "Nous n’avons pas pu vérifier cette invitation à l’équipe de pratique pour le moment."
              : "We could not verify this Practice Team invitation right now."
          )
        );
      } finally {
        if (alive) setLoading(false);
      }
    }

    if (invite.token && invite.email) {
      openInvite();
    } else {
      setLoading(false);
      setError(
        t(
          "settings.practiceTeam.invite.errors.missing",
          isFrench
            ? "Il manque des informations requises dans ce lien d’invitation à l’équipe de pratique."
            : "This Practice Team invitation link is missing required details."
        )
      );
    }

    return () => {
      alive = false;
    };
  }, [invite.email, invite.proof, invite.token, t]);

  async function continueLiveInvite() {
    const next = `/practice-team/accept?token=${encodeURIComponent(invite.token)}&email=${encodeURIComponent(invite.email)}`;
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user?.id) {
      navigate(next);
      return;
    }

    const params = new URLSearchParams();
    params.set("sheet", "1");
    params.set("email", invite.email);
    params.set("next", next);
    params.set("invite", "practice_team");
    navigate(`/login?${params.toString()}`);
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]">
      <main className="afrimed-rail afrimed-rail--comfortable pt-3 pb-12">
        <TopBar
          title={t("settings.practiceTeam.invite.title", isFrench ? "Invitation à l’équipe de pratique" : "Practice Team invitation")}
          subtitle={t(
            "settings.practiceTeam.invite.subtitle",
            isFrench
              ? "Consultez votre invitation à l’équipe de pratique AfriMed™. L’accès reste limité pendant la validation du soutien délégué."
              : "Review your AfriMed™ Practice Team invite. Access remains limited while delegated practice support is in staging."
          )}
          onBack={() => navigate(backPath)}
          titleWrapperClassName="mt-2 mb-5"
        />

        <div className="w-full max-w-[1120px] space-y-6">
          <p className="text-body-medium text-on-surface-var">
            {t(
              "settings.practiceTeam.invite.body",
              isFrench
                ? "Cette invitation prépare uniquement votre accès à l’équipe de pratique. Elle ne donne pas accès aux dossiers patients, messages, rendez-vous, paiements, ordonnances ni actions cliniques."
                : "This invitation prepares your Practice Team access only. It does not grant access to patient records, messages, appointments, payments, prescriptions or clinical actions."
            )}
          </p>

          {loading ? (
            <p className="text-body-medium text-on-surface-var">
              {t("common.loading", isFrench ? "Chargement..." : "Loading...")}
            </p>
          ) : null}

          {error ? (
            <p className="text-body-medium text-[rgb(var(--afrimed-danger))]">{error}</p>
          ) : null}

          {invite.proof ? (
            <p className="text-body-medium text-on-surface-var">
              {t(
                "settings.practiceTeam.invite.proofCopy",
                isFrench
                  ? "Il s’agit d’un lien de preuve pour la revue fondateur. Aucun accès à l’équipe de pratique ne sera activé depuis cette preuve."
                  : "This is a proof link for founder review. No Practice Team access will be activated from this proof."
              )}
            </p>
          ) : null}

          <TextField56
            tone="surface"
            label={t("settings.practiceTeam.invite.emailLabel", isFrench ? "E-mail invité" : "Invited email")}
            value={
              invite.email ||
              t("settings.practiceTeam.invite.emailMissing", isFrench ? "Aucun e-mail invité n’est associé à ce lien." : "No invited email was attached to this link.")
            }
            onChange={() => {}}
            readOnly
          />

          {inviteDetails ? (
            <div className="space-y-2 text-body-medium text-on-surface-var">
              <p>
                {t("settings.practiceTeam.invite.practiceLabel", isFrench ? "Pratique" : "Practice")}:{" "}
                <span className="text-on-surface">{inviteDetails.practice_name}</span>
              </p>
              <p>
                {t("settings.practiceTeam.invite.roleLabel", isFrench ? "Rôle" : "Role")}:{" "}
                <span className="text-on-surface">{inviteDetails.role_label}</span>
              </p>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={loading || !!error}
              onClick={() => {
                if (invite.proof) {
                  navigate(`${returnPath}?proof=reviewed`);
                  return;
                }
                void continueLiveInvite();
              }}
            >
              {invite.proof
                ? t("settings.practiceTeam.invite.proofCta", isFrench ? "Retourner à l’équipe de pratique" : "Return to Practice Team")
                : t("settings.practiceTeam.invite.cta", isFrench ? "Continuer vers la connexion" : "Continue to sign in")}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
